import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  TrendingDown,
  FileText,
  Calendar,
  Search,
  DollarSign,
  Package,
  ShoppingCart,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowRight
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  LineChart,
  Line,
  PieChart, 
  Pie, 
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const TOOLTIP_STYLE = {
  backgroundColor: '#13192B',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: '#fff'
};

// Mini sparkline component
const MiniSparkline = ({ data, color = "#C6E76C" }) => (
  <div className="h-8 w-full mt-2">
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data}>
        <defs>
          <linearGradient id="sparkGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area 
          type="monotone" 
          dataKey="value" 
          stroke={color} 
          strokeWidth={1.5}
          fill="url(#sparkGradient)" 
          dot={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

// Metric card component
const MetricCard = ({ icon: Icon, label, value, change, sparklineData, iconColor, onClick }) => {
  const isPositive = change >= 0;
  
  return (
    <div
      onClick={onClick}
      data-testid={`metric-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`bg-[#13192B] border border-white/8 rounded-lg p-5 hover:-translate-y-[1px] hover:border-white/15 transition-all duration-200 ${onClick ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs uppercase tracking-[0.1em] text-gray-500 font-semibold">{label}</span>
        <div className={`p-2 rounded-md ${iconColor}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      
      <div className="text-4xl tracking-tighter font-light text-white mb-1">
        {value}
      </div>
      
      {typeof change === 'number' && (
        <div className="flex items-center gap-2 text-sm">
          {isPositive ? (
            <TrendingUp className="w-3.5 h-3.5 text-[#C6E76C]" />
          ) : (
            <TrendingDown className="w-3.5 h-3.5 text-[#F87171]" />
          )}
          <span className={isPositive ? 'text-[#C6E76C]' : 'text-[#F87171]'}>
            {isPositive ? '+' : ''}{change}%
          </span>
          <span className="text-gray-500">vs last week</span>
        </div>
      )}
      
      {sparklineData && <MiniSparkline data={sparklineData} />}
    </div>
  );
};

export default function DashboardNew() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    newApplications: 0,
    dropoffsToday: 0,
    inspectionsToday: 0,
    outstandingPayouts: 0,
    liveItems: 0,
    salesThisMonth: { count: 0, revenue: 0 }
  });
  const [attentionItems, setAttentionItems] = useState([]);
  const [todaysSchedule, setTodaysSchedule] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [revenueTotals, setRevenueTotals] = useState({ revenue: 0, sales_count: 0 });
  const [revenueDays, setRevenueDays] = useState(30);
  const [pipelineData, setPipelineData] = useState([]);
  const [formStats, setFormStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/analytics/form-stats`, { withCredentials: true })
      .then(res => setFormStats(res.data))
      .catch(() => {});
  }, []);

  const loadRevenue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/revenue?days=${revenueDays}`, { withCredentials: true });
      const byDate = {};
      (res.data.daily_breakdown || []).forEach(d => { byDate[d._id] = d.revenue; });
      const series = [];
      const now = new Date();
      for (let i = revenueDays - 1; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 86400000);
        const key = d.toISOString().slice(0, 10);
        series.push({
          date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          revenue: byDate[key] || 0
        });
      }
      setRevenueData(series);
      setRevenueTotals(res.data.totals || { revenue: 0, sales_count: 0 });
    } catch (error) {
      toast.error('Failed to load revenue data');
    }
  }, [revenueDays]);

  useEffect(() => {
    loadRevenue();
  }, [loadRevenue]);


  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load all dashboard data in parallel
      const [summaryRes, attentionRes, todayRes, activityRes, applicationsRes] = await Promise.all([
        axios.get(`${API}/dashboard/summary`, { withCredentials: true }),
        axios.get(`${API}/dashboard/attention`, { withCredentials: true }),
        axios.get(`${API}/dashboard/today`, { withCredentials: true }),
        axios.get(`${API}/dashboard/recent-activity?limit=5`, { withCredentials: true }),
        axios.get(`${API}/applications?limit=100`, { withCredentials: true })
      ]);

      setMetrics({
        newApplications: summaryRes.data.new_applications || 0,
        dropoffsToday: summaryRes.data.dropoffs_today || 0,
        inspectionsToday: summaryRes.data.inspections_today || 0,
        outstandingPayouts: summaryRes.data.outstanding_payouts || 0,
        liveItems: summaryRes.data.live_items || 0,
        salesThisMonth: summaryRes.data.sales_this_month || { count: 0, revenue: 0 }
      });

      setAttentionItems(attentionRes.data || []);
      setTodaysSchedule(todayRes.data.bookings || []);
      setRecentActivity(activityRes.data || []);

      // Build pipeline data from applications
      const apps = applicationsRes.data || [];
      const stageCounts = {};
      apps.forEach(app => {
        const stage = app.lifecycle_stage;
        stageCounts[stage] = (stageCounts[stage] || 0) + 1;
      });

      const pipelineDataMapped = [
        { name: 'Awaiting Drop-off', value: stageCounts['awaiting_dropoff'] || 0, color: '#60A5FA' },
        { name: 'Awaiting Check-in', value: stageCounts['awaiting_checkin'] || 0, color: '#34D399' },
        { name: 'In Preparation', value: stageCounts['in_preparation'] || 0, color: '#FBBF24' },
        { name: 'Live on Marketplace', value: stageCounts['live_on_marketplace'] || 0, color: '#A78BFA' },
        { name: 'Sold', value: stageCounts['sold'] || 0, color: '#C6E76C' }
      ];

      setPipelineData(pipelineDataMapped.filter(d => d.value > 0));

    } catch (error) {
      toast.error('Some dashboard data failed to load. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'application_submitted':
      case 'application':
        return <FileText className="w-4 h-4" />;
      case 'item_sold':
      case 'sale':
        return <DollarSign className="w-4 h-4" />;
      case 'inspection':
        return <Search className="w-4 h-4" />;
      case 'payout':
        return <CheckCircle2 className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'application_submitted':
      case 'application':
        return 'bg-blue-500/20 text-blue-400';
      case 'item_sold':
      case 'sale':
        return 'bg-[#C6E76C]/20 text-[#C6E76C]';
      case 'inspection':
        return 'bg-purple-500/20 text-purple-400';
      case 'payout':
        return 'bg-yellow-500/20 text-yellow-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  // Generate mock sparkline data
  const handleExport = () => {
    const rows = [
      ['Listers Dashboard Export', new Date().toLocaleString('en-AU')],
      [],
      ['Metric', 'Value'],
      ['New Applications', metrics.newApplications],
      ['Drop-offs Today', metrics.dropoffsToday],
      ['Inspections Today', metrics.inspectionsToday],
      ['Outstanding Payouts', metrics.outstandingPayouts],
      ['Live Items', metrics.liveItems],
      ['Sales This Month (count)', metrics.salesThisMonth.count],
      ['Sales This Month (revenue)', metrics.salesThisMonth.revenue],
      [`Revenue (last ${revenueDays} days)`, revenueTotals.revenue],
      [`Sales count (last ${revenueDays} days)`, revenueTotals.sales_count],
      [],
      ['Date', 'Daily Revenue'],
      ...revenueData.map(d => [d.date, d.revenue])
    ];
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `listers-dashboard-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0A0F1E]">
        <div className="text-white">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="bg-[#0A0F1E] min-h-screen p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl tracking-tight font-medium text-white mb-1">Dashboard</h1>
          <p className="text-sm text-gray-400">Overview of your business operations</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="px-4 py-2 bg-[#13192B] border border-white/10 rounded-lg text-sm text-gray-300 hover:border-white/20 transition-all" data-testid="dashboard-date-range">
            <Calendar className="w-4 h-4 inline mr-2" />
            {new Date(Date.now() - (revenueDays - 1) * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </button>
          <button onClick={handleExport} data-testid="dashboard-export-button" className="px-4 py-2 bg-[#C6E76C] text-black rounded-lg text-sm font-medium hover:bg-[#C6E76C] transition-all">
            Export
          </button>
        </div>
      </div>

      {/* Top Metrics Row */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <MetricCard
          icon={FileText}
          label="New Applications"
          value={metrics.newApplications}
          iconColor="bg-emerald-500/20 text-emerald-400"
          onClick={() => navigate('/applications-list')}
        />
        <MetricCard
          icon={Calendar}
          label="Drop-offs Today"
          value={metrics.dropoffsToday}
          iconColor="bg-blue-500/20 text-blue-400"
          onClick={() => navigate('/calendar', { state: { view: 'day' } })}
        />
        <MetricCard
          icon={Search}
          label="Inspections Today"
          value={metrics.inspectionsToday}
          iconColor="bg-purple-500/20 text-purple-400"
          onClick={() => navigate('/calendar', { state: { view: 'day' } })}
        />
        <MetricCard
          icon={DollarSign}
          label="Outstanding Payouts"
          value={formatCurrency(metrics.outstandingPayouts)}
          iconColor="bg-yellow-500/20 text-yellow-400"
          onClick={() => navigate('/payouts')}
        />
        <MetricCard
          icon={Package}
          label="Live Items"
          value={metrics.liveItems}
          iconColor="bg-cyan-500/20 text-cyan-400"
          onClick={() => navigate('/live-items')}
        />
        <MetricCard
          icon={ShoppingCart}
          label="Sales This Month"
          value={`${metrics.salesThisMonth.count} / ${formatCurrency(metrics.salesThisMonth.revenue)}`}
          iconColor="bg-pink-500/20 text-pink-400"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Needs Attention */}
        <div className="bg-[#13192B] border border-white/8 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <h2 className="text-xl tracking-tight font-medium text-white">Needs Your Attention</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4">Items requiring immediate action</p>

          {/* Critical Items */}
          <div className="mb-4">
            <h3 className="text-xs uppercase tracking-[0.1em] text-red-400 font-semibold mb-3">
              CRITICAL ({attentionItems.critical?.length || 0})
            </h3>
            <div className="space-y-2">
              {(attentionItems.critical || []).slice(0, 4).map((item) => (
                <div 
                  key={item.id}
                  onClick={() => navigate(item.application_id ? `/application/${item.application_id}` : `/item/${item.item_id}`)}
                  className="p-3 bg-[#0A0F1E] border border-white/5 rounded-lg hover:border-red-400/30 hover:bg-red-500/5 cursor-pointer transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white mb-1">{item.title}</p>
                      <p className="text-xs text-gray-500">{item.reference_code} • {item.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded">
                        CRITICAL
                      </span>
                      <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-red-400 transition-colors" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* High Priority Items */}
          <div>
            <h3 className="text-xs uppercase tracking-[0.1em] text-orange-400 font-semibold mb-3">
              HIGH PRIORITY ({attentionItems.high_priority?.length || 0})
            </h3>
            <div className="space-y-2">
              {(attentionItems.high_priority || []).slice(0, 3).map((item) => (
                <div 
                  key={item.id}
                  onClick={() => navigate(item.application_id ? `/application/${item.application_id}` : `/item/${item.item_id}`)}
                  className="p-3 bg-[#0A0F1E] border border-white/5 rounded-lg hover:border-orange-400/30 hover:bg-orange-500/5 cursor-pointer transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white mb-1">{item.title}</p>
                      <p className="text-xs text-gray-500">{item.reference_code} • {item.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs font-medium rounded">
                        HIGH
                      </span>
                      <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-orange-400 transition-colors" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button 
            onClick={() => navigate('/tasks')}
            className="mt-4 w-full py-2 text-sm text-[#C6E76C] hover:text-[#C6E76C] font-medium flex items-center justify-center gap-2 transition-colors"
          >
            View all tasks
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Middle Column - Today's Schedule & Recent Activity */}
        <div className="space-y-6">
          {/* Today's Schedule */}
          <div className="bg-[#13192B] border border-white/8 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-blue-400" />
              <h2 className="text-xl tracking-tight font-medium text-white">Today&apos;s Schedule</h2>
            </div>
            <p className="text-xs text-gray-500 mb-4">{todaysSchedule.length} bookings</p>

            <div className="space-y-3">
              {todaysSchedule.slice(0, 6).map((booking, idx) => (
                <div key={booking.id} className="flex items-start gap-3 pb-3 border-b border-white/5 last:border-0">
                  <div className="text-right min-w-[70px]">
                    <p className="text-sm font-medium text-white">{booking.time_slot || booking.start_time?.split('T')[1]?.substring(0, 5) || '9:00 AM'}</p>
                  </div>
                  <div className={`w-10 h-10 rounded-md ${booking.type === 'dropoff' ? 'bg-blue-500/20' : 'bg-green-500/20'} flex items-center justify-center`}>
                    {booking.type === 'dropoff' ? (
                      <Calendar className="w-5 h-5 text-blue-400" />
                    ) : (
                      <Search className="w-5 h-5 text-[#C6E76C]" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white capitalize">{booking.type || 'Drop-off'}</p>
                    <p className="text-xs text-gray-500">{booking.title || booking.customer_name || 'Customer'}</p>
                  </div>
                </div>
              ))}
            </div>

            <button 
              onClick={() => navigate('/calendar')}
              className="mt-4 w-full py-2 text-sm text-[#C6E76C] hover:text-[#C6E76C] font-medium flex items-center justify-center gap-2 transition-colors"
            >
              View full calendar
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Recent Activity */}
          <div className="bg-[#13192B] border border-white/8 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-purple-400" />
              <h2 className="text-xl tracking-tight font-medium text-white">Recent Activity</h2>
            </div>

            <div className="space-y-4 relative">
              <div className="absolute left-[15px] top-0 bottom-0 w-px bg-white/10"></div>
              
              {recentActivity.map((activity, idx) => (
                <div key={activity.id || idx} className="flex items-start gap-3 relative">
                  <div className={`w-8 h-8 rounded-full ${getActivityColor(activity.action_type)} flex items-center justify-center z-10`}>
                    {getActivityIcon(activity.action_type)}
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="text-sm text-white mb-1">{activity.description}</p>
                    <p className="text-xs text-gray-500">{activity.created_at ? new Date(activity.created_at).toLocaleTimeString() : '2m ago'}</p>
                  </div>
                </div>
              ))}
            </div>

            <button 
              onClick={() => navigate('/applications-list')}
              className="mt-4 w-full py-2 text-sm text-[#C6E76C] hover:text-[#C6E76C] font-medium flex items-center justify-center gap-2 transition-colors"
            >
              View all activity
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Right Column - Revenue Overview & Pipeline */}
        <div className="space-y-6">
          {/* Revenue Overview */}
          <div className="bg-[#13192B] border border-white/8 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl tracking-tight font-medium text-white">Revenue Overview</h2>
                <p className="text-xs text-gray-500">Last {revenueDays} days</p>
              </div>
              <select
                value={revenueDays}
                onChange={(e) => setRevenueDays(Number(e.target.value))}
                className="bg-[#0A0F1E] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-gray-300 focus:outline-none focus:border-[#C6E76C]/50"
                data-testid="revenue-days-select"
              >
                <option value={30}>30 Days</option>
                <option value={7}>7 Days</option>
                <option value={90}>90 Days</option>
              </select>
            </div>

            <div className="mb-4">
              <p className="text-4xl tracking-tighter font-light text-white" data-testid="revenue-total">{formatCurrency(revenueTotals.revenue)}</p>
              <div className="flex items-center gap-2 mt-2">
                <TrendingUp className="w-4 h-4 text-[#C6E76C]" />
                <span className="text-sm text-gray-500">{revenueTotals.sales_count} sales in the last {revenueDays} days</span>
              </div>
            </div>

            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#C6E76C" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#C6E76C" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#475569" 
                    fontSize={11}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="#475569" 
                    fontSize={11}
                    tickLine={false}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#C6E76C" 
                    strokeWidth={2}
                    fill="url(#revenueGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Application Pipeline */}
          <div className="bg-[#13192B] border border-white/8 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 bg-[#C6E76C] rounded-full animate-pulse"></div>
              <h2 className="text-xl tracking-tight font-medium text-white">Application Pipeline</h2>
            </div>

            <div className="flex items-center justify-center mb-6">
              <div className="relative w-48 h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pipelineData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pipelineData.map((entry) => (
                        <Cell key={`cell-${entry.name}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-3xl font-light text-white">
                      {pipelineData.reduce((sum, d) => sum + d.value, 0)}
                    </p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {pipelineData.map((item) => (
                <div key={item.name} className="flex items-center justify-between p-2 rounded hover:bg-white/[0.02]">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                    <span className="text-sm text-gray-300">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-white">{item.value}</span>
                    <span className="text-xs text-gray-500">
                      ({Math.round(item.value / pipelineData.reduce((sum, d) => sum + d.value, 0) * 100)}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <button 
              onClick={() => navigate('/applications-list')}
              className="mt-4 w-full py-2 text-sm text-[#C6E76C] hover:text-[#C6E76C] font-medium flex items-center justify-center gap-2 transition-colors"
            >
              View all applications
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Form Funnel */}
          {formStats && (
            <div className="bg-[#13192B] border border-white/8 rounded-lg p-6" data-testid="form-funnel-card">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 bg-[#C6E76C] rounded-full"></div>
                <h2 className="text-xl tracking-tight font-medium text-white">Application Form Funnel</h2>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-[#0A0F1E] border border-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-light text-white" data-testid="funnel-starts">{formStats.last_30_days.starts}</p>
                  <p className="text-xs text-gray-500 mt-1">Started</p>
                </div>
                <div className="bg-[#0A0F1E] border border-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-light text-white" data-testid="funnel-submits">{formStats.last_30_days.submits}</p>
                  <p className="text-xs text-gray-500 mt-1">Submitted</p>
                </div>
                <div className="bg-[#0A0F1E] border border-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-light text-[#C6E76C]" data-testid="funnel-conversion">
                    {formStats.last_30_days.conversion_pct != null ? `${formStats.last_30_days.conversion_pct}%` : '—'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Conversion</p>
                </div>
              </div>
              <p className="text-xs text-gray-500">Last 30 days • All time: {formStats.all_time.starts} started, {formStats.all_time.submits} submitted{formStats.all_time.conversion_pct != null ? ` (${formStats.all_time.conversion_pct}%)` : ''}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
